import { useState, useEffect } from "react";
import { differenceInDays } from "date-fns";
import { useParams } from "react-router-dom";
import axios from "axios";
import useFetch from "../../clients/features/get_withcredentials";
import { TbCheck, TbUser, TbBed, TbCalendar, TbReceipt, TbPlus, TbMinus } from "react-icons/tb";

const STEPS = [
  { id: 1, label: "Client", icon: TbUser },
  { id: 2, label: "Chambre", icon: TbBed },
  { id: 3, label: "Dates & Tarif", icon: TbCalendar },
  { id: 4, label: "Confirmation", icon: TbReceipt },
];

const Counter = ({ label, hint, value, onChange, min = 0, max = 10 }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
    <div>
      <p className="text-sm font-semibold text-gray-800">{label}</p>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
      >
        <TbMinus className="text-xs" />
      </button>
      <span className="w-5 text-center text-sm font-bold text-gray-900">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
      >
        <TbPlus className="text-xs" />
      </button>
    </div>
  </div>
);

export default function ManualReservation() {
  const { hotelId } = useParams();
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);

  const { data: rooms = [] } = useFetch(
    `https://api.komorotel.com/api/rooms/hotel/${hotelId}`
  );

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedRatePlan, setSelectedRatePlan] = useState(null);
  const [ratePlans, setRatePlans] = useState([]);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [total, setTotal] = useState(null);
  const [totalKMF, setTotalKMF] = useState(null);
  const [pricePerNight, setPricePerNight] = useState(null);
  const [pricePerNightKMF, setPricePerNightKMF] = useState(null);
  const childMaxAge = selectedRatePlan?.extraGuestPolicy?.childMaxAge ?? 12;

  const [guests, setGuests] = useState({ adults: 1, children: 0 });

  const [guest, setGuest] = useState({
    firstName: "", lastName: "", email: "", phone: ""
  });

  const nights = checkIn && checkOut
    ? differenceInDays(new Date(checkOut), new Date(checkIn))
    : 0;

  // Fetch rate plans liés à cette room
  useEffect(() => {
    if (!selectedRoom) return;

    const fetchRatePlans = async () => {
      try {
        const ratePricesRes = await axios.get(
          `https://api.komorotel.com/api/rateprices/room/${selectedRoom._id}`,
          { withCredentials: true }
        );

        const ratePlanIds = [
          ...new Set(ratePricesRes.data.map(rp =>
            (rp.ratePlanId?._id || rp.ratePlanId).toString()
          ))
        ];

        if (ratePlanIds.length === 0) {
          setRatePlans([]);
          setSelectedRatePlan(null);
          return;
        }

        const allPlansRes = await axios.get(
          `https://api.komorotel.com/api/rateplans/hotel/${hotelId}`,
          { withCredentials: true }
        );

        const filtered = allPlansRes.data.filter(plan =>
          ratePlanIds.includes(plan._id.toString())
        );

        setRatePlans(filtered);
        setSelectedRatePlan(filtered[0] || null);
      } catch (err) {
        console.error(err);
      }
    };

    fetchRatePlans();
  }, [selectedRoom]);

  // Calcul prix — se relance si guests change aussi
  useEffect(() => {
    if (!selectedRoom || !selectedRatePlan || !checkIn || !checkOut) return;

    const fetchPrice = async () => {
      try {
        setLoadingPrice(true);
        const res = await axios.get(
          "https://api.komorotel.com/api/rateprices/calculate",
          {
            params: {
              roomId: selectedRoom._id,
              ratePlanId: selectedRatePlan._id,
              checkIn,
              checkOut,
              adults: guests.adults,
              children: guests.children
            }
          }
        );
        setTotal(res.data.total);
        setTotalKMF(res.data.totalKMF);
        setPricePerNight(res.data.pricePerNight);
        setPricePerNightKMF(res.data.pricePerNightKMF);
      } catch (err) {
        setTotal(null);
        setTotalKMF(null);
      } finally {
        setLoadingPrice(false);
      }
    };

    fetchPrice();
  }, [selectedRoom, selectedRatePlan, checkIn, checkOut, guests]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await axios.post(
        "https://api.komorotel.com/api/reservation/admin",
        {
          roomId: selectedRoom._id,
          ratePlanId: selectedRatePlan?._id,
          hotelId,
          checkInDate: checkIn,
          checkOutDate: checkOut,
          guests,
          guest: {
            firstName: guest.firstName,
            lastName: guest.lastName,
            email: guest.email || `admin-${Date.now()}@komorotel.com`,
            phone: guest.phone
          }
        },
        { withCredentials: true }
      );

      setSuccess(true);
      setStep(1);
      setSelectedRoom(null);
      setCheckIn("");
      setCheckOut("");
      setTotal(null);
      setTotalKMF(null);
      setGuests({ adults: 1, children: 0 });
      setGuest({ firstName: "", lastName: "", email: "", phone: "" });
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const canNext = () => {
    if (step === 1) return guest.firstName && guest.lastName;
    if (step === 2) return !!selectedRoom;
    if (step === 3) return checkIn && checkOut && nights > 0 && selectedRatePlan && total && !loadingPrice;    return true;
  };

  return (
    <div className="max-w-2xl mx-auto">

      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Nouvelle réservation</h1>
        <p className="text-sm text-gray-400 mt-1">Créez une réservation manuellement</p>
      </div>

      {/* SUCCESS */}
      {success && (
        <div className="mb-6 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 font-semibold flex items-center gap-2">
          <TbCheck className="text-lg" />
          Réservation créée avec succès
        </div>
      )}

      {/* STEPPER */}
      <div className="flex items-center mb-8">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = step === s.id;
          const isDone = step > s.id;
          return (
            <div key={s.id} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all
                  ${isDone ? "bg-[#3A9D23] text-white" : isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-400"}
                `}>
                  {isDone ? <TbCheck className="text-lg" /> : <Icon className="text-lg" />}
                </div>
                <span className={`text-xs mt-1.5 font-medium ${isActive ? "text-gray-900" : isDone ? "text-[#3A9D23]" : "text-gray-400"}`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-5 transition-all ${isDone ? "bg-[#3A9D23]" : "bg-gray-100"}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* CONTENT */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-4">

        {/* STEP 1 — CLIENT */}
        {step === 1 && (
          <div className="flex flex-col gap-6">
            <div>
              <h2 className="text-base font-bold text-gray-900 mb-4">Informations client</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Prénom *</label>
                  <input
                    value={guest.firstName}
                    onChange={(e) => setGuest({ ...guest, firstName: e.target.value })}
                    placeholder="Prénom"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Nom *</label>
                  <input
                    value={guest.lastName}
                    onChange={(e) => setGuest({ ...guest, lastName: e.target.value })}
                    placeholder="Nom"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Email</label>
                  <input
                    value={guest.email}
                    onChange={(e) => setGuest({ ...guest, email: e.target.value })}
                    placeholder="email@exemple.com"
                    type="email"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Téléphone</label>
                  <input
                    value={guest.phone}
                    onChange={(e) => setGuest({ ...guest, phone: e.target.value })}
                    placeholder="+269..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
                  />
                </div>
              </div>
            </div>

            
          </div>
        )}

        {/* STEP 2 — CHAMBRE */}
        {step === 2 && (
          <div>
            <h2 className="text-base font-bold text-gray-900 mb-5">Choisir une chambre</h2>
            {rooms.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Aucune chambre disponible</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {rooms.map((room) => (
                  <div
                    key={room._id}
                    onClick={() => setSelectedRoom(room)}
                    className={`
                      p-4 rounded-xl border cursor-pointer transition
                      ${selectedRoom?._id === room._id
                        ? "border-[#3A9D23] bg-green-50"
                        : "border-gray-100 hover:border-gray-200"
                      }
                    `}
                  >
                    <p className="font-semibold text-gray-900 text-sm">{room.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{room.maxPeople} personnes max</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — DATES + TARIF */}
        {step === 3 && (
          <div className="flex flex-col gap-6">

            <div>
              <h2 className="text-base font-bold text-gray-900 mb-4">Dates du séjour</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Arrivée *</label>
                  <input
                    type="date"
                    value={checkIn}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setCheckIn(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Départ *</label>
                  <input
                    type="date"
                    value={checkOut}
                    min={checkIn || new Date().toISOString().split("T")[0]}
                    onChange={(e) => setCheckOut(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-base font-bold text-gray-900 mb-4">Tarif</h2>
              {ratePlans.length === 0 ? (
                <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                  ⚠️ Aucun tarif configuré pour cette chambre.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {ratePlans.map((plan) => (
                    <div
                      key={plan._id}
                      onClick={() => setSelectedRatePlan(plan)}
                      className={`
                        flex items-center justify-between p-3 rounded-xl border cursor-pointer transition
                        ${selectedRatePlan?._id === plan._id
                          ? "border-[#3A9D23] bg-green-50"
                          : "border-gray-100 hover:border-gray-200"
                        }
                      `}
                    >
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{plan.name}</p>
                        <p className={`text-xs mt-0.5 ${
                          plan.cancellationPolicy?.type === "free" ? "text-green-600" :
                          plan.cancellationPolicy?.type === "partial" ? "text-yellow-600" :
                          "text-red-500"
                        }`}>
                          {plan.cancellationPolicy?.type === "free" && "Annulation gratuite"}
                          {plan.cancellationPolicy?.type === "partial" && "Annulation partielle"}
                          {plan.cancellationPolicy?.type === "non_refundable" && "Non remboursable"}
                        </p>
                      </div>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        selectedRatePlan?._id === plan._id ? "border-[#3A9D23]" : "border-gray-300"
                      }`}>
                        {selectedRatePlan?._id === plan._id && (
                          <div className="w-2 h-2 rounded-full bg-[#3A9D23]" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Occupants */}
            <div>
              <h2 className="text-base font-bold text-gray-900 mb-1">Voyageurs</h2>
              <p className="text-xs text-gray-400 mb-3">Le nombre d'occupants peut influencer le prix selon le tarif sélectionné</p>
              <div className="bg-gray-50 rounded-xl px-4 py-1 border border-gray-100">
                <Counter
                  label="Adultes"
                  hint={`${childMaxAge + 1} ans et plus`}
                  value={guests.adults}
                  onChange={(v) => setGuests((g) => ({ ...g, adults: v }))}
                  min={1}
                  max={10}
                />
                <Counter
                  label="Enfants"
                  hint={`Moins de ${childMaxAge + 1} ans`}
                  value={guests.children}
                  onChange={(v) => setGuests((g) => ({ ...g, children: v }))}
                  min={0}
                  max={10}
                />
              </div>
            </div>

            {/* Prix calculé en temps réel */}
            {checkIn && checkOut && nights > 0 && selectedRatePlan && (
              <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                {loadingPrice ? (
                  <p className="text-xs text-gray-400">Calcul en cours...</p>
                ) : total ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400">{nights} nuit{nights > 1 ? "s" : ""} · {guests.adults} adulte{guests.adults > 1 ? "s" : ""}{guests.children > 0 ? ` · ${guests.children} enfant${guests.children > 1 ? "s" : ""}` : ""}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{pricePerNight} € / nuit</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">{total} €</p>
                      <p className="text-xs text-gray-400">{totalKMF?.toLocaleString("fr-FR")} KMF</p>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

          </div>
        )}

        {/* STEP 4 — RÉCAP */}
        {step === 4 && (
          <div>
            <h2 className="text-base font-bold text-gray-900 mb-5">Récapitulatif</h2>
            <div className="flex flex-col">
              {[
                { label: "Client", value: `${guest.firstName} ${guest.lastName}` },
                ...(guest.email ? [{ label: "Email", value: guest.email }] : []),
                ...(guest.phone ? [{ label: "Téléphone", value: guest.phone }] : []),
                { label: "Voyageurs", value: `${guests.adults} adulte${guests.adults > 1 ? "s" : ""}${guests.children > 0 ? ` · ${guests.children} enfant${guests.children > 1 ? "s" : ""}` : ""}` },
                { label: "Chambre", value: selectedRoom?.title },
                {
                  label: "Dates",
                  value: `${new Date(checkIn + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} → ${new Date(checkOut).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`
                },
                { label: "Nuits", value: nights },
                { label: "Tarif", value: selectedRatePlan?.name },
                ...(pricePerNight ? [{ label: "Prix / nuit", value: `${pricePerNight} € · ${pricePerNightKMF?.toLocaleString("fr-FR")} KMF` }] : []),
              ].map((item, i) => (
                <div key={i} className="flex justify-between text-sm py-3 border-b border-gray-50">
                  <span className="text-gray-400">{item.label}</span>
                  <span className="font-semibold text-gray-900">{item.value}</span>
                </div>
              ))}

              <div className="flex items-end justify-between pt-4">
                <span className="font-bold text-gray-900">Total</span>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    {loadingPrice ? "..." : `${total} €`}
                  </p>
                  {totalKMF && (
                    <p className="text-xs text-gray-400">{totalKMF.toLocaleString("fr-FR")} KMF</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* NAVIGATION */}
      <div className="flex gap-3">
        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
          >
            ← Retour
          </button>
        )}

        {step < 4 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canNext()}
            className={`
              flex-1 py-3 rounded-xl text-sm font-semibold transition
              ${!canNext()
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-900 text-white hover:bg-gray-700"
              }
            `}
          >
            Suivant →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`
              flex-1 py-3 rounded-xl text-sm font-semibold transition
              ${loading
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#3A9D23] text-white hover:bg-[#33891E] active:scale-[0.98]"
              }
            `}
          >
            {loading ? "Création..." : "✅ Confirmer la réservation"}
          </button>
        )}
      </div>

    </div>
  );
}